/**
 * Motore di calcolo fiscale forfettario — imposta, INPS, DPPI, saldo, acconti, rateazione.
 * Tutte le formule seguono il principio di cassa (incassi, non fatturato).
 * Costanti centralizzate in src/config/fiscalConstants.js (fonte di verità unica).
 */

import {
  SOGLIA_ACCONTO_UNICO,
  SPLIT_ACCONTI,
  INTERESSE_RATEAZIONE_MENSILE,
  ALIQUOTA_DPPI,
  MASSIMALI_INPS,
  ALIQUOTA_IMPOSTA_AGEVOLATA,
  ALIQUOTA_IMPOSTA_STANDARD,
  ANNI_ALIQUOTA_AGEVOLATA,
} from "../config/fiscalConstants";
import { getAliquotaINPS } from "../constants/aliquote";
import { FORFETTARIO_THRESHOLD, FORFETTARIO_THRESHOLD_EXIT } from "../constants/ateco";

// Arrotonda a 2 decimali (centesimi di euro).
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ───────── Anni di attività e aliquota imposta sostitutiva ─────────
// L'anno è SEMPRE un parametro esplicito: l'aliquota di un anno dipende da
// quanti anni di attività erano trascorsi IN QUELL'ANNO, non da oggi. Guardare
// il 2025 nel 2030 deve restituire l'aliquota che il 2025 aveva davvero.

// Anno più antico accettato come apertura P.IVA. Serve a scartare i valori che
// Number() converte silenziosamente in numeri privi di senso come anno:
// null, "", "  ", false e [] danno 0; true dà 1. Senza questa soglia un utente
// con impostazioni incomplete risulterebbe al "2026° anno" e quindi al 15%.
const ANNO_PIVA_MINIMO = 1900;

// Anni di attività maturati nell'anno indicato (1 = primo anno).
// Input non utilizzabile (assente, null, stringa vuota, non numerico, anno
// implausibile) o anno precedente all'apertura → 1, cioè lo stesso stato di un
// utente appena registrato (DEFAULT_FISCAL_SETTINGS.pivaYearStart = anno
// corrente). Non restituisce mai NaN: a valle finirebbe silenziosamente
// nell'imposta, e `NaN <= 5` è false, cioè 15%.
export function calcolaAnniAttivita(pivaYearStart, anno) {
  const inizio = Number(pivaYearStart);
  const riferimento = Number(anno);
  const valido = (n) => Number.isFinite(n) && n >= ANNO_PIVA_MINIMO;
  if (!valido(inizio) || !valido(riferimento)) return 1;
  return Math.max(1, Math.trunc(riferimento) - Math.trunc(inizio) + 1);
}

// Aliquota imposta sostitutiva (in punti percentuali) dell'anno indicato.
export function getAliquotaSostitutiva(pivaYearStart, anno) {
  return calcolaAnniAttivita(pivaYearStart, anno) <= ANNI_ALIQUOTA_AGEVOLATA
    ? ALIQUOTA_IMPOSTA_AGEVOLATA
    : ALIQUOTA_IMPOSTA_STANDARD;
}

// ───────── Ragguaglio soglie primo anno di attività ─────────
// Nel primo anno di attività i limiti del forfettario vanno ragguagliati ai giorni
// effettivi di attività: limite = soglia × (giorni / 365).
// Es. P.IVA aperta il 1° luglio → 184 giorni → 85.000 × 184/365 = 42.849,32 €.

// Data di apertura P.IVA in formato ISO "YYYY-MM-DD".
// Retrocompatibilità: se `pivaStartDate` non è impostata, ricade sul 1° gennaio
// dell'anno indicato da `pivaYearStart` (nessun ragguaglio).
export function getPivaStartDate(fiscalSettings = {}) {
  if (fiscalSettings.pivaStartDate) return fiscalSettings.pivaStartDate;
  const anno = fiscalSettings.pivaYearStart || new Date().getFullYear();
  return `${anno}-01-01`;
}

// Giorni di attività nell'anno indicato (dalla data di apertura al 31/12 inclusi).
// Ritorna 365 (anno pieno) se l'anno è successivo a quello di apertura, 0 se precedente.
export function calcolaGiorniAttivita(pivaStartDate, anno) {
  if (!pivaStartDate) return 365;
  const start = new Date(`${pivaStartDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return 365;

  const annoApertura = start.getFullYear();
  if (anno > annoApertura) return 365;
  if (anno < annoApertura) return 0;

  const fineAnno = new Date(anno, 11, 31);
  return Math.round((fineAnno - start) / 86400000) + 1;
}

// Soglia ragguagliata ai giorni di attività per l'anno indicato.
// Anni successivi al primo → soglia piena.
export function calcolaSogliaRagguagliata(pivaStartDate, anno, sogliaBase = FORFETTARIO_THRESHOLD) {
  const base = Number(sogliaBase) || 0;
  const giorni = calcolaGiorniAttivita(pivaStartDate, anno);
  if (giorni >= 365) return base;
  if (giorni <= 0) return base;
  return round2(base * (giorni / 365));
}

// Helper unico per i consumer: entrambe le soglie (85k uscita anno successivo e
// 100k uscita immediata) ragguagliate, più i metadati per la UI.
export function getSogliePerAnno(fiscalSettings = {}, anno = new Date().getFullYear()) {
  const pivaStartDate = getPivaStartDate(fiscalSettings);
  const giorniAttivita = calcolaGiorniAttivita(pivaStartDate, anno);
  const isRagguagliata = giorniAttivita > 0 && giorniAttivita < 365;

  return {
    soglia: calcolaSogliaRagguagliata(pivaStartDate, anno, FORFETTARIO_THRESHOLD),
    sogliaExit: calcolaSogliaRagguagliata(pivaStartDate, anno, FORFETTARIO_THRESHOLD_EXIT),
    giorniAttivita,
    isRagguagliata,
    pivaStartDate,
  };
}

// ───────── Formule esplicite (calcolo P.IVA non al primo anno) ─────────
// Sede unica dei calcoli fiscali annuali. I consumer (Dashboard, FiscalSummaryCard)
// devono chiamare calcolaRiepilogoFiscale, non ricalcolare inline.

// Imponibile forfettario = incassi × coefficiente di redditività.
export function calcolaImponibile(incassi, coefficiente) {
  return round2((Number(incassi) || 0) * (Number(coefficiente) || 0));
}

// INPS dovuto = imponibile × aliquota INPS (DECIMALE, es. 0.2607).
export function calcolaInpsDovuto(imponibile, aliquotaInps) {
  return round2((Number(imponibile) || 0) * (Number(aliquotaInps) || 0));
}

// INPS da pagare = INPS dovuto − acconti INPS versati (>= 0).
export function calcolaInpsDaPagare(inpsDovuto, accontiInpsVersati) {
  return round2(Math.max(0, (Number(inpsDovuto) || 0) - (Number(accontiInpsVersati) || 0)));
}

// Tasse lorde = (imponibile − contributi INPS versati deducibili) × aliquota imposta (DECIMALE).
export function calcolaTasseLorde(imponibile, contributiVersati, aliquotaImposta) {
  const base = Math.max(0, (Number(imponibile) || 0) - (Number(contributiVersati) || 0));
  return round2(base * (Number(aliquotaImposta) || 0));
}

// Tasse da pagare = tasse lorde − acconti tasse versati (>= 0).
export function calcolaTasseDaPagare(tasseLorde, accontiTasseVersati) {
  return round2(Math.max(0, (Number(tasseLorde) || 0) - (Number(accontiTasseVersati) || 0)));
}

// Accantonamento = quanto mettere da parte per coprire il dovuto (cap agli incassi).
export function calcolaAccantonamento(incassi, totaleDovuto) {
  return round2(Math.min(Number(incassi) || 0, Math.max(0, Number(totaleDovuto) || 0)));
}

// Netto disponibile = incassi − totale dovuto.
export function calcolaNettoDisponibile(incassi, totaleDovuto) {
  return round2((Number(incassi) || 0) - (Number(totaleDovuto) || 0));
}

/**
 * Risolve i versamenti dell'anno combinando F24 pagati e valori dichiarati in
 * configurazione (bootstrap di onboarding, salvato in fiscalByYear).
 *
 * La fonte è scelta PER COMPONENTE (INPS / imposta), non per record: un F24 che
 * non contiene righe INPS non deve cancellare i contributi dichiarati, altrimenti
 * registrare un pagamento farebbe SALIRE le tasse.
 *
 * Regola: massimo fra le due fonti, mai somma. Spesso descrivono lo stesso
 * versamento (l'F24 documenta ciò che il bootstrap dichiarava), quindi sommarle
 * lo conterebbe due volte; inoltre gli F24 sono aggregati per annoRiferimento,
 * che non coincide con l'anno di pagamento rilevante per la deduzione per cassa.
 * Poiché il termine F24 è monotòno crescente, aggiungere un F24 non può far
 * scendere il versato né la deduzione: le tasse non possono aumentare.
 *
 * @param {object} fiscalData - record di getFiscalDataForYear(anno).
 */
export function risolviVersamentiAnno(fiscalData = {}) {
  const accInps = fiscalData.inpsAccontiVersati || { giugno: 0, novembre: 0 };
  const accImp = fiscalData.accontiVersati || { giugno: 0, novembre: 0 };
  const saldoV = fiscalData.saldoVersato || { imposta: 0, inps: 0 };

  const f24AccontiInps = (accInps.giugno || 0) + (accInps.novembre || 0);
  const f24AccontiTasse = (accImp.giugno || 0) + (accImp.novembre || 0);
  const f24Inps = f24AccontiInps + (saldoV.inps || 0);
  const f24Imposta = f24AccontiTasse + (saldoV.imposta || 0);

  // Valori dichiarati in configurazione (onboarding / carry-over).
  const contributiManuali = Number(fiscalData.contributiInpsVersati) || 0;
  const accontiManuali = Number(fiscalData.accontiVersatiManuali) || 0;

  const versatoInps = Math.max(f24Inps, contributiManuali);
  const versatoImposta = Math.max(f24Imposta, accontiManuali);

  // Quota conteggiata che NESSUN F24 documenta: viene dai valori dichiarati in
  // configurazione e resta correggibile dall'utente. Serve alle viste per dire
  // da dove arriva il versato.
  const versatoDaConfigurazione =
    (versatoInps > f24Inps ? versatoInps : 0) + (versatoImposta > f24Imposta ? versatoImposta : 0);

  const haF24 = f24Inps + f24Imposta > 0;
  const fonte = !haF24 ? "manuale" : versatoDaConfigurazione > 0 ? "misto" : "f24";

  return {
    fonte,
    accontiInpsVersati: round2(f24AccontiInps),
    accontiTasseVersati: round2(Math.max(f24AccontiTasse, accontiManuali)),
    contributiVersati: round2(versatoInps), // INPS deducibile
    versatoTotale: round2(versatoInps + versatoImposta),
    versatoDaConfigurazione: round2(versatoDaConfigurazione),
  };
}

/**
 * Riepilogo fiscale annuale completo — orchestratore unico.
 * @param {object} p
 * @param {number} p.incassi - incassi annui (principio di cassa).
 * @param {number} p.coefficiente - coefficiente di redditività (es. 0.78).
 * @param {number} p.aliquotaInps - aliquota INPS in DECIMALE (es. 0.2607).
 * @param {number} p.aliquotaImposta - aliquota imposta sostitutiva in DECIMALE (0.05/0.15).
 * @param {object} p.fiscalData - record di getFiscalDataForYear(anno) per i versamenti.
 */
export function calcolaRiepilogoFiscale({
  incassi,
  coefficiente,
  aliquotaInps,
  aliquotaImposta,
  fiscalData = {},
}) {
  const v = risolviVersamentiAnno(fiscalData);
  const imponibile = calcolaImponibile(incassi, coefficiente);
  const inpsDovuto = calcolaInpsDovuto(imponibile, aliquotaInps);
  const inpsDaPagare = calcolaInpsDaPagare(inpsDovuto, v.accontiInpsVersati);
  const tasseLorde = calcolaTasseLorde(imponibile, v.contributiVersati, aliquotaImposta);
  const tasseDaPagare = calcolaTasseDaPagare(tasseLorde, v.accontiTasseVersati);
  const totaleDovuto = round2(inpsDovuto + tasseLorde);
  const residuo = round2(Math.max(0, totaleDovuto - v.versatoTotale));
  return {
    imponibile,
    inpsDovuto,
    inpsDaPagare,
    tasseLorde,
    tasseDaPagare,
    totaleDovuto,
    versatoTotale: v.versatoTotale,
    residuo,
    accantonamento: calcolaAccantonamento(incassi, totaleDovuto),
    nettoDisponibile: calcolaNettoDisponibile(incassi, totaleDovuto),
    fonteVersamenti: v.fonte,
    versatoDaConfigurazione: v.versatoDaConfigurazione,
  };
}

/**
 * Imposta sostitutiva dovuta sull'anno.
 * @deprecated usare calcolaRiepilogoFiscale / calcolaTasseLorde.
 * Imponibile = (incassi × coefficiente redditività) − contributi deducibili (>= 0).
 * Imposta = imponibile × aliquota. contributiDeducibili default 0 (retrocompatibile).
 */
export function calcolaImpostaDovuta(incassiAnno, coefficienteRedditivita, aliquota, contributiDeducibili = 0) {
  const imponibile = Math.max(0, incassiAnno * coefficienteRedditivita - (Number(contributiDeducibili) || 0));
  return round2(imponibile * aliquota);
}

/**
 * Aliquota INPS in forma DECIMALE (es. 0.2623) per tipo gestione e anno,
 * derivata da getAliquotaINPS (che restituisce punti percentuali, es. 26.23).
 * Bridge verso la fonte unica delle aliquote (src/constants/aliquote.js).
 */
export function getAliquotaInpsDecimal(inpsType, anno) {
  return getAliquotaINPS(inpsType, anno) / 100;
}

/**
 * Contributi INPS dovuti sull'anno (gestione separata o cassa a percentuale).
 * @param {number} aliquotaInps - aliquota in forma DECIMALE (es. 0.2623).
 *   Usare getAliquotaInpsDecimal(inpsType, anno) per derivarla dalla fonte unica.
 */
export function calcolaInpsDovuta(incassiAnno, coefficienteRedditivita, aliquotaInps) {
  const imponibile = incassiAnno * coefficienteRedditivita;
  return round2(imponibile * aliquotaInps);
}

/**
 * Contributo aggiuntivo DPPI: 1% (NON 10%) sull'eccedenza del massimale INPS
 * gestione separata. Se il massimale non è noto (null) restituisce 0.
 */
export function calcolaDPPI(imponibile, massimale) {
  if (massimale == null || imponibile <= massimale) return 0;
  return round2((imponibile - massimale) * ALIQUOTA_DPPI);
}

/**
 * Massimale INPS gestione separata per anno (null se non ancora definito).
 */
export function getMassimaleInps(anno) {
  return MASSIMALI_INPS[anno] ?? null;
}

/**
 * Saldo anno N (da versare a giugno N+1) = imposta dovuta − acconti versati anno N.
 */
export function calcolaSaldo(impostaDovutaAnnoN, accontiVersatiAnnoN) {
  const totaleAcconti =
    (accontiVersatiAnnoN?.giugno || 0) + (accontiVersatiAnnoN?.novembre || 0);
  return round2(Math.max(0, impostaDovutaAnnoN - totaleAcconti));
}

/**
 * Acconti anno N+1 (metodo storico: 100% imposta anno N), split giugno/novembre.
 * Sotto la soglia (257,52 €) si versa in unica soluzione a novembre.
 */
export function calcolaAcconti(impostaDovutaAnnoN) {
  const totaleAcconto = impostaDovutaAnnoN;
  if (totaleAcconto <= SOGLIA_ACCONTO_UNICO) {
    return { giugno: 0, novembre: round2(totaleAcconto) };
  }
  return {
    giugno: round2(totaleAcconto * SPLIT_ACCONTI.giugno),
    novembre: round2(totaleAcconto * SPLIT_ACCONTI.novembre),
  };
}

/**
 * Rata di giugno = saldo anno N−1 + primo acconto anno N.
 */
export function calcolaRataGiugno(saldoAnnoPrec, accontiAnnoCorrente) {
  return round2(saldoAnnoPrec + (accontiAnnoCorrente?.giugno || 0));
}

/**
 * Rata di novembre = secondo acconto anno N.
 */
export function calcolaRataNovembre(accontiAnnoCorrente) {
  return round2(accontiAnnoCorrente?.novembre || 0);
}

/**
 * Interessi rateazione (codice tributo 1668): 0,33%/mese, prima rata = 0.
 */
export function calcolaInteressiRateazione(importoTotale, numeroRate, numeroRataCorrente) {
  if (numeroRataCorrente === 1) return 0;
  const importoRata = importoTotale / numeroRate;
  const capitaleResiduo = importoTotale - importoRata * (numeroRataCorrente - 1);
  return round2(
    (capitaleResiduo * INTERESSE_RATEAZIONE_MENSILE * (numeroRataCorrente - 1)) / numeroRate
  );
}
